import {
  Button,
  Card,
  Segmented,
  SkeletonLoader,
  Status,
} from '@insight-health-ai/roseate/components';
import {
  ReloadOutlined,
  CaretUpOutlined,
  CaretDownOutlined,
  MinusOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import {
  ChevronDown,
  MinusCircle,
  TrendDown01,
  TrendUp01,
} from '@insight-health-ai/roseate/icons';
import classNames from 'classnames';
import { useState } from 'react';
import { useGetAppointmentMetrics } from 'src/hooks/appointment';
import dayjs from 'dayjs';
import TrendUpSvg from 'src/assets/trend_up.svg';
import TrendDownSvg from 'src/assets/trend_down.svg';
import { Tooltip } from 'antd';
import { useScreenSize } from '@insight-health-ai/roseate/utils';

export const AppointmentMetrics = () => {
  const { mediaMax } = useScreenSize();
  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState<'monthly' | 'weekly'>(
    'monthly'
  );
  const {
    data: appointmentMetrics,
    isLoading: isLoadingMetrics,
    isFetching: isFetchingMetrics,
    refetch,
  } = useGetAppointmentMetrics();

  const handleToggleExpand = () => {
    if (isLoadingMetrics) {
      return;
    }
    setIsExpanded(!isExpanded);
  };

  const renderTitle = () => {
    if (isLoadingMetrics) {
      return (
        <div className="flex justify-between items-center gap-2">
          <SkeletonLoader
            active
            paragraph={{ rows: 1, width: 300 }}
            title={false}
          />
        </div>
      );
    }
    return (
      <div
        className="flex justify-between items-center gap-2 cursor-pointer"
        onClick={handleToggleExpand}
      >
        <span className="font-semibold">Summary </span>
        {!isExpanded && (
          <div className="flex items-center gap-6">
            {appointmentMetrics?.summary_data?.[selectedPeriod]
              ?.slice(0, 3)
              .map(({ title, value, trend }, index) => (
                <div key={index} className="flex items-center gap-2">
                  <span className="text-sm text-gray-500">
                    {' '}
                    {mediaMax('lg')
                      ? title.replace('Appointments ', '')
                      : title}
                  </span>
                  <span className="text-sm font-semibold">{value}</span>
                  <Status
                    size="xs"
                    appearance={
                      trend > 0 ? 'success' : trend < 0 ? 'error' : 'neutral'
                    }
                    icon={
                      trend > 0 ? (
                        <TrendUp01 />
                      ) : trend < 0 ? (
                        <TrendDown01 />
                      ) : (
                        <MinusCircle />
                      )
                    }
                  >
                    {trend}
                  </Status>
                </div>
              ))}
          </div>
        )}
        <div className="flex items-center gap-2 flex-shrink-0">
          <Segmented
            className="font-normal"
            value={selectedPeriod}
            onChange={(value) =>
              setSelectedPeriod(value as 'monthly' | 'weekly')
            }
            options={[
              { label: 'Monthly', value: 'monthly' },
              { label: 'Weekly', value: 'weekly' },
            ]}
          />
          <Button
            variant="text"
            appearance="secondary"
            className="flex-shrink-0"
            disabled={!appointmentMetrics || isLoadingMetrics}
            icon={
              <ChevronDown
                className={classNames(
                  'text-lg transition-transform duration-200',
                  { 'rotate-180': isExpanded }
                )}
              />
            }
          />
        </div>
      </div>
    );
  };

  const renderChildren = () => {
    if (isLoadingMetrics || !isExpanded || !appointmentMetrics) {
      return null;
    }

    const metrics =
      appointmentMetrics.summary_data[
        selectedPeriod as 'monthly' | 'weekly'
      ]?.slice(0, 3) || [];

    return (
      <div className="flex flex-col">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 p-3">
          {metrics.map(({ title, value, trend }, index) => {
            const isPositive = trend > 0;
            const isNeutral = trend === 0;
            return (
              <Card key={index} padding={{ body: 'sm' }}>
                <div className="flex flex-col gap-2">
                  <span className="text-sm text-gray-500 font-medium">
                    {title}
                  </span>
                  <div className="flex justify-between">
                    <div className="flex items-baseline gap-2">
                      <span className="text-2xl font-semibold">{value}</span>
                      <Status
                        size="xs"
                        appearance={
                          isPositive
                            ? 'success'
                            : isNeutral
                              ? 'neutral'
                              : 'error'
                        }
                        icon={
                          isPositive ? (
                            <CaretUpOutlined />
                          ) : isNeutral ? (
                            <MinusOutlined />
                          ) : (
                            <CaretDownOutlined />
                          )
                        }
                      >
                        {isNeutral
                          ? 'no change'
                          : `${Math.abs(trend)} from last`}{' '}
                        {!isNeutral &&
                          (selectedPeriod === 'monthly' ? 'month' : 'week')}
                      </Status>
                    </div>
                    {!isNeutral && (
                      <img
                        src={isPositive ? TrendUpSvg : TrendDownSvg}
                        alt={isPositive ? 'Trend up' : 'Trend down'}
                        className="h-6 w-auto"
                      />
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
        <div className="flex gap-3 items-center justify-end w-full border-t border-gray-200 py-3 px-4">
          <span className="text-sm text-gray-500 flex items-center gap-2">
            <Tooltip title="Refresh data" placement="left">
              {isFetchingMetrics ? (
                <LoadingOutlined />
              ) : (
                <ReloadOutlined
                  className="cursor-pointer"
                  onClick={() => refetch()}
                />
              )}
            </Tooltip>
            Last updated:{' '}
            {dayjs(appointmentMetrics?.last_updated).format(
              'MMM D, YYYY h:mm a'
            )}
          </span>
        </div>
      </div>
    );
  };

  const children = renderChildren();

  return (
    <Card
      className="flex-shrink-0"
      padding={{ body: 'none', header: 'sm' }}
      title={renderTitle()}
    >
      {children}
    </Card>
  );
};
